import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Callout, InlineError } from '@/components/ui/Feedback';
import { TextInput } from '@/components/ui/Form';
import { api } from '@/lib/api';

/**
 * Sign in with Discord (§3).
 *
 * The button is a plain link to the API, not a fetch: the OAuth2 flow is a
 * browser redirect, and doing it any other way breaks the state cookie handling.
 */
export function SignInPage() {
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? '/';
  const oauthError = params.get('error');

  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold text-text">Development Hub</h1>
          <p className="mt-1 text-sm text-text-muted">Spring Side Network</p>
        </div>

        <div className="rounded-[--radius-panel] border border-border bg-surface p-5 shadow-panel">
          {oauthError && (
            <Callout
              tone="critical"
              className="mb-4"
              icon={<AlertTriangle aria-hidden className="size-3.5" />}
              title="Discord sign-in failed"
            >
              {oauthError}
            </Callout>
          )}

          <p className="text-sm text-text-muted">
            Sign in with the Discord account you use on the Spring Side Network server. Your
            development role decides what you can see and do.
          </p>

          <a
            href={`/api/auth/discord/start?redirect=${encodeURIComponent(redirect)}`}
            className="mt-5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-accent px-4 text-sm font-medium text-white shadow-card transition-colors hover:bg-accent-hover"
          >
            <LogIn aria-hidden className="size-4" />
            Continue with Discord
          </a>

          <DevSignIn />
        </div>

        <p className="mt-4 text-center text-[11px] text-text-subtle">
          Access is granted through Discord roles. Ask a Development Director if you need it.
        </p>
      </div>
    </div>
  );
}

/**
 * The local development sign-in.
 *
 * Deliberately unglamorous and always visible in the built app — if the server
 * has it disabled (the default, and impossible to enable in production) the
 * request is simply refused, so this cannot become a way in by accident.
 */
function DevSignIn() {
  const [open, setOpen] = useState(false);
  const [discordId, setDiscordId] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 w-full text-center text-[11px] text-text-subtle underline-offset-2 hover:underline"
      >
        Local development sign-in
      </button>
    );
  }

  return (
    <form
      className="mt-5 space-y-3 border-t border-border pt-4"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.post('/api/auth/dev-login', { discordId: discordId.trim() });
          window.location.href = '/';
        } catch (caught) {
          setError(caught);
        } finally {
          setBusy(false);
        }
      }}
    >
      <TextInput
        label="Development account ID"
        value={discordId}
        onChange={(event) => setDiscordId(event.target.value)}
        placeholder="900000000000000001"
        hint="Only works when AUTH_DEV_LOGIN is enabled. Never available in production."
        required
      />
      <InlineError error={error} />
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button type="submit" size="sm" variant="secondary" loading={busy}>
          Sign in
        </Button>
      </div>
    </form>
  );
}
